import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type FxName = "tick" | "thunk" | "wipe" | "lock";

interface SoundContextValue {
  enabled: boolean;
  toggle: () => void;
  playFx: (name: FxName) => void;
}

const SoundContext = createContext<SoundContextValue>({
  enabled: false,
  toggle: () => {},
  playFx: () => {},
});

export const useSound = () => useContext(SoundContext);

const STORAGE_KEY = "brick:sound";
const SOURCES: Record<FxName, string> = {
  tick:  "/audio/tick.mp3",
  thunk: "/audio/thunk.mp3",
  wipe:  "/audio/wipe.mp3",
  lock:  "/audio/lock.mp3",
};

/**
 * Optional sound design layer. Off by default. State persisted in
 * localStorage. Audio elements are created lazily on first toggle-on.
 */
export const SoundProvider = ({ children }: { children: ReactNode }) => {
  const [enabled, setEnabled] = useState(false);
  const cacheRef = useRef<Partial<Record<FxName, HTMLAudioElement>>>({});

  useEffect(() => {
    try {
      setEnabled(localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, []);

  const ensure = useCallback((name: FxName) => {
    if (!cacheRef.current[name]) {
      const a = new Audio(SOURCES[name]);
      a.preload = "auto";
      a.volume = 0.4;
      cacheRef.current[name] = a;
    }
    return cacheRef.current[name]!;
  }, []);

  const playFx = useCallback(
    (name: FxName) => {
      if (!enabled) return;
      try {
        const a = ensure(name);
        a.currentTime = 0;
        void a.play().catch(() => undefined);
      } catch {
        /* ignore */
      }
    },
    [enabled, ensure],
  );

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <SoundContext.Provider value={{ enabled, toggle, playFx }}>{children}</SoundContext.Provider>
  );
};
